// NOTE: A pure base64 encoder, because this package must run under both Vite
// and Metro. `btoa` is a browser global the package forbids, and React Native
// 0.86 does not polyfill it or `TextEncoder` — so a shared helper that reached
// for either would type-check on web and crash on a phone.
//
// The string encoder encodes UTF-8 first, which is also what HTTP Basic
// credentials are supposed to carry (RFC 7617). `btoa` cannot do that: it
// throws on any character above U+00FF and silently emits Latin-1 bytes for
// U+0080-U+00FF. The bytes encoder under it takes raw bytes as they are, such
// as a SHA-256 digest (NSAI-6953).

const BASE64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

const PADDING_CHARACTER = "=";

const SURROGATE_RANGE_START = 0xd800;
const SURROGATE_RANGE_END = 0xdfff;
const REPLACEMENT_CODE_POINT = 0xfffd;

// NOTE: Walks code points rather than UTF-16 units so an astral character
// (emoji, rare CJK) is encoded as one 4-byte sequence instead of two lone
// surrogates.
const toUtf8Bytes = (value: string): number[] => {
  const bytes: number[] = [];

  for (let index = 0; index < value.length; index += 1) {
    const rawCodePoint = value.codePointAt(index) ?? 0;

    if (rawCodePoint > 0xffff) {
      // NOTE: codePointAt consumed both halves of the surrogate pair.
      index += 1;
    }

    // NOTE: codePointAt only returns a value in the surrogate range when the
    // unit is unpaired, which is not encodable. Standard encoders (TextEncoder,
    // Buffer) substitute U+FFFD there rather than emitting the raw surrogate as
    // CESU-8, and Basic credentials have to match them byte for byte.
    const codePoint =
      rawCodePoint >= SURROGATE_RANGE_START &&
      rawCodePoint <= SURROGATE_RANGE_END
        ? REPLACEMENT_CODE_POINT
        : rawCodePoint;

    if (codePoint < 0x80) {
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }

  return bytes;
};

// NOTE: NSAI-6953 — encodes raw bytes as standard (padded, non-URL-safe)
// base64: the alphabet with `+` and `/`, padded with `=` to a multiple of four
// characters. A `Uint8Array` and a plain number array both work. A value
// outside 0-255 keeps only its low byte, as `Uint8Array` and `Buffer` do.
export const encodeBytesBase64 = (bytes: ArrayLike<number>): string => {
  let encoded = "";

  for (let index = 0; index < bytes.length; index += 3) {
    // NOTE: The last group can be one or two bytes short. A missing byte reads
    // as 0 here and is written as padding below.
    const firstByte = (bytes[index] ?? 0) & 0xff;
    const secondByte = (bytes[index + 1] ?? 0) & 0xff;
    const thirdByte = (bytes[index + 2] ?? 0) & 0xff;

    const triple = (firstByte << 16) | (secondByte << 8) | thirdByte;
    const remaining = bytes.length - index;

    encoded += BASE64_ALPHABET[(triple >> 18) & 0x3f] ?? "";
    encoded += BASE64_ALPHABET[(triple >> 12) & 0x3f] ?? "";
    encoded +=
      remaining > 1
        ? (BASE64_ALPHABET[(triple >> 6) & 0x3f] ?? "")
        : PADDING_CHARACTER;
    encoded +=
      remaining > 2
        ? (BASE64_ALPHABET[triple & 0x3f] ?? "")
        : PADDING_CHARACTER;
  }

  return encoded;
};

// NOTE: Encodes a string as standard (padded, non-URL-safe) base64 of its UTF-8
// bytes.
export const encodeUtf8Base64 = (value: string): string =>
  encodeBytesBase64(toUtf8Bytes(value));

// NOTE: NSAI-6847 — base64url (RFC 4648 §5): the alphabet JWT segments are
// written in, with `-` and `_` for `+` and `/`, and padding left off.
const BASE64URL_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

const BITS_PER_BASE64_CHARACTER = 6;
const BITS_PER_BYTE = 8;
// NOTE: At most 6 carried bits plus the 6 a character adds.
const BIT_BUFFER_MASK = 0xfff;

// NOTE: Decodes base64url into the UTF-8 string it carries, padded or not.
// null when the input is not base64url, or its bytes are not UTF-8.
//
// Pure for the same reason the encoder is: `atob` is a browser global this
// package keeps out. The bytes go through `decodeURIComponent`, plain
// ECMAScript rather than the host's `TextDecoder`, which throws on malformed
// UTF-8 instead of guessing.
export const decodeUtf8Base64Url = (encoded: string): string | null => {
  const unpadded = encoded.replace(/=+$/, "");
  let percentEncoded = "";
  let bitBuffer = 0;
  let bitCount = 0;

  for (const character of unpadded) {
    const sextet = BASE64URL_ALPHABET.indexOf(character);

    if (sextet === -1) {
      return null;
    }

    bitBuffer =
      ((bitBuffer << BITS_PER_BASE64_CHARACTER) | sextet) & BIT_BUFFER_MASK;
    bitCount += BITS_PER_BASE64_CHARACTER;

    if (bitCount >= BITS_PER_BYTE) {
      bitCount -= BITS_PER_BYTE;
      const byte = (bitBuffer >> bitCount) & 0xff;
      percentEncoded += `%${byte.toString(16).padStart(2, "0")}`;
    }
  }

  try {
    return decodeURIComponent(percentEncoded);
  } catch {
    return null;
  }
};
